import React, { FC } from 'react';

import { Routes } from '@common/constants';
import SectionContainer from '@common/components/SectionContainer';
import { useRouter } from 'shared-routing';
import { useTranslations } from '@lang';
import { useCheckForArrivalDate } from '@common/hooks';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';

import { SpaListTemplate } from 'voyager';

import { SpaTreatmentsV2Props } from './SpaTreatmentsV2.types';
import { useSpaEntityFilter } from './hooks/useSpaEntityFilter';
import { useSpaTreatmentsList } from './hooks/useSpaTreatmentsList';

export const SpaTreatmentsV2: FC<SpaTreatmentsV2Props> = ({
  property,
  journeyParams,
  changeJourneyParam,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_TREATMENT_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    changeJourneyParam,
    listingRoute: Routes.SPA_TREATMENT_LIST,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, isFetching } = useSpaTreatmentsList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const { data: treatmentsCategories = [] } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );

  const categoryOptions = buildCategoryOptions(treatmentsCategories, t('common.allCategories'));

  const handleSelectTreatment = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
  };

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="spa"
            categories={navCategories}
            category="treatments"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={isFetching ? [] : listItems}
          onSelectItem={handleSelectTreatment}
          isLoading={isFetching}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          titleLabel={t('app.spaTreatments')}
          categoryLabel={t('common.category')}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('spa.noResults')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
